<template>
  <HeaderBar />

  <section class="carvia-container space-y-4">
    <div class="grid grid-cols-1 gap-4 md:grid-cols-4">
      <Input v-model="referenceNumberQuery" variant="ghost" :placeholder="t('referenceNumber')" />
      <Select
        v-model="selectedInsuranceProvider"
        :options="insuranceProviderOptions"
        empty-value-to="undefined"
        variant="ghost"
      />
      <CVButton :to="{ name: 'insuranceCase', params: { id: 'new' } }" class="md:col-start-3">{{
        t('createInsuranceCase')
      }}</CVButton>
    </div>

    <div class="flex items-end justify-between">
      <Tabs v-model="activeTab" :tabs="tabs" />
    </div>

    <div class="flex flex-col gap-2">
      <InsuranceCaseListItem
        v-for="insuranceCase in insuranceCases"
        :key="insuranceCase.id"
        :insurance-case="insuranceCase"
      />
    </div>

    <EntityListExceptions
      :status="status"
      :error="error"
      :items-count="insuranceCases.length"
      :no-items-text="t('noInsuranceCasesFound')"
    />

    <div v-if="hasNextPage" class="flex justify-center pt-8">
      <CVButton outline :is-loading="isFetchingNextPage" @click="() => fetchNextPage()">
        {{ t('loadMore') }}
      </CVButton>
    </div>
  </section>
</template>

<script setup lang="ts">
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import EntityListExceptions from '@/components/EntityListExceptions.vue';
import InsuranceCaseListItem from './InsuranceCaseListItem.vue';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import { useInsuranceCases } from '@/queries/use-insurance-cases';
import {
  FindAllInsuranceCasesSort,
  type InsuranceCase,
  InsuranceCaseStatus,
} from '@carvia/ros-client-types';
import { computed, ref, watch, watchEffect } from 'vue';
import Tabs, { type TabItem } from '@/components/Tabs.vue';
import Input from '@/components/Input.vue';
import { useI18n } from 'vue-i18n';
import { useUiStore } from '@/stores/ui.store';
import { refDebounced } from '@vueuse/core';
import Select from '@/components/Select.vue';
import { useRouteQueryRef } from '@/hooks/use-route-query-ref';
import { useInsuranceProviderOptions } from '../insurance-case/use-insurance-provider-options';

enum Tab {
  INITIAL = 'INITIAL',
  BOOKED = 'BOOKED',
  ACTIVE = 'ACTIVE',
  TO_CLOSE = 'TO_CLOSE',
  ALL = 'ALL',
}

const { t } = useI18n();
const uiStore = useUiStore();
const activeTab = ref<Tab>(Tab.INITIAL);

watchEffect(() => {
  uiStore.setHeaderTitle(t('insuranceCases'));
});

const referenceNumberQuery = useRouteQueryRef('referenceNumber', '', 'string');
const debouncedReferenceNumberQuery = refDebounced(referenceNumberQuery, 500);

watch(
  referenceNumberQuery,
  () => {
    if (!referenceNumberQuery.value) {
      return;
    }
    activeTab.value = Tab.ALL;
  },
  { immediate: true },
);

const filters: Record<Tab, InsuranceCaseStatus[]> = {
  [Tab.INITIAL]: [InsuranceCaseStatus.INITIAL],
  [Tab.BOOKED]: [InsuranceCaseStatus.BOOKED],
  [Tab.ACTIVE]: [InsuranceCaseStatus.HANDED_OVER],
  [Tab.TO_CLOSE]: [InsuranceCaseStatus.RETURNED],
  [Tab.ALL]: [],
};

const selectedInsuranceProvider = useRouteQueryRef('insuranceProvider', undefined, 'string');
const insuranceProviderOptions = useInsuranceProviderOptions(true);

const query = computed(() => ({
  status: filters[activeTab.value],
  referenceNumber: debouncedReferenceNumberQuery.value || undefined,
  sort: [FindAllInsuranceCasesSort.END_DATE],
  insuranceProvider: selectedInsuranceProvider.value as
    | InsuranceCase['insuranceProvider']
    | undefined,
}));

watch(activeTab, () => {
  if (activeTab.value !== Tab.ALL) {
    referenceNumberQuery.value = '';
  }
});

const { data, fetchNextPage, hasNextPage, isFetchingNextPage, status, error } =
  useInsuranceCases(query);

const insuranceCases = useFlattenPaginatedData(data);

const tabs = computed<TabItem[]>(() => [
  {
    title: t('initial'),
    id: Tab.INITIAL,
  },
  {
    title: t('booked'),
    id: Tab.BOOKED,
  },
  {
    title: t('active'),
    id: Tab.ACTIVE,
  },
  {
    title: t('toClose'),
    id: Tab.TO_CLOSE,
  },
  {
    title: t('all'),
    id: Tab.ALL,
  },
]);
</script>

<i18n lang="json">
{
  "en": {
    "insuranceCases": "Insurance cases",
    "noInsuranceCasesFound": "No insurance cases found",
    "loadMore": "Load more",
    "initial": "Initial",
    "booked": "Booked",
    "active": "Active",
    "toClose": "To close",
    "all": "All",
    "createInsuranceCase": "+ Create",
    "referenceNumber": "Reference Number"
  },
  "de": {
    "insuranceCases": "Versicherungsfälle",
    "noInsuranceCasesFound": "Keine Versicherungsfälle gefunden",
    "loadMore": "Mehr laden",
    "initial": "Initial",
    "booked": "Gebucht",
    "active": "Aktiv",
    "toClose": "Abzuschließen",
    "all": "Alle",
    "createInsuranceCase": "+ Anlegen",
    "referenceNumber": "Aktenzeichen / Vorgangsnummer"
  },
  "es": {
    "insuranceCases": "Siniestros",
    "noInsuranceCasesFound": "No se encontraron siniestros",
    "loadMore": "Cargar más",
    "initial": "Inicial",
    "booked": "Reservado",
    "active": "Activo",
    "toClose": "Por cerrar",
    "all": "Todos",
    "createInsuranceCase": "+ Crear",
    "referenceNumber": "N.º de expediente / referencia"
  }
}
</i18n>
